import { type MediaClientConfig } from '@atlaskit/media-client';
import { collabStateManager } from '@databases/collab-state-manager';
import {
  type AttachmentReadPolicy,
  type AttachmentPresentation
} from '@databases/field-read-modules/attachment';
import {
  getMediaClient,
  getMediaCollectionId
} from '@databases/media-client/media-client-core';
import { type DefaultStore } from '@databases/redux';
import {
  getTemplateImageIdentifier,
  isTemplateImageCover
} from '@databases/templates/utils';
import pLimit from 'p-limit';
import { isMigrationPlaceholderMediaId } from '@canvas/media-id';

/**
 * Application-owned media context, prepared before the source-neutral view mounts.
 */
export type AttachmentFrontendAdapter = Required<AttachmentReadPolicy> & {
  readonly mediaClientConfig: MediaClientConfig | undefined;
};

/**
 * Shares collection lookup and bounds metadata work within one source lifetime.
 * Rejections belong to the resource store's error state, not loaded absence.
 */
export function createAttachmentFrontendAdapter({
  contentId,
  store,
  hideMigrationPlaceholders,
  enforceDataSecurityPolicy = true
}: {
  readonly contentId: string;
  readonly store: Pick<DefaultStore, 'getState'>;
  readonly hideMigrationPlaceholders: boolean;
  readonly enforceDataSecurityPolicy?: boolean;
}): AttachmentFrontendAdapter {
  const manager = collabStateManager.getCollabManager(contentId);
  const client = manager ? getMediaClient(manager, enforceDataSecurityPolicy) : undefined;
  let collection: Promise<string | undefined> | undefined;
  const limit = pLimit(8);

  return Object.freeze({
    mediaClientConfig: client?.mediaClientConfig,
    getAttachmentResource: (fileId: string) =>
      Object.freeze({
        key: `attachment:${contentId}:${fileId}`,
        load: (signal: AbortSignal): Promise<AttachmentPresentation | undefined> =>
          limit(async () => {
            if (
              signal.aborted ||
              !client ||
              (hideMigrationPlaceholders && isMigrationPlaceholderMediaId(fileId))
            ) {
              return undefined;
            }

            collection ??= getMediaCollectionId(store.getState(), contentId);

            const collectionName = await collection;

            if (signal.aborted || !collectionName) {
              return undefined;
            }

            if (isTemplateImageCover(fileId)) {
              const identifier = getTemplateImageIdentifier(fileId);

              return Object.freeze({
                source: 'external-image',
                collectionName,
                url: identifier.dataURI,
                name: identifier.name ?? ''
              });
            }

            const file = await client.mediaStore.getFile(fileId, {
              collection: collectionName
            });

            return signal.aborted
              ? undefined
              : Object.freeze({
                  source: 'media-file',
                  fileId,
                  collectionName,
                  mediaType: file.data.mediaType
                });
          })
      })
  });
}
